import fallbackLensIcon from "../../assets/lenses/fallback-lens-icon.png";
import {
    type AppInfo,
    type MyLensesAppVersion,
    MyLensesAuthOperation,
    MyLensesPlatformType,
} from "../../../gql-generated/types";

type OrganizationApps = Partial<AppInfo>;

export enum AppVersionStatus {
    ENABLED = "ENABLED",
    DISABLED = "DISABLED",
    UNKNOWN = "UNKNOWN",
}

export class AppListItemModel {
    public readonly iconUrl?: string;
    public readonly name?: string;
    public readonly organization?: string;
    public readonly prodStatus?: AppVersionStatus;
    public readonly stageStatus?: AppVersionStatus;
    public readonly prodLiveVersion?: string;
    public readonly stagingLiveVersion?: string;
    public readonly appId?: string;
    public readonly versions?: readonly MyLensesAppVersion[];
    public readonly authorizedOperations?: readonly string[];
    public readonly platformType?: MyLensesPlatformType;

    public static createAppListItem({
        appIconUrl,
        internalAppName,
        prodLiveVersion,
        stagingLiveVersion,
        versions,
        appId,
        appAuthorizedOperations,
        platformType,
    }: OrganizationApps): AppListItemModel {
        return {
            iconUrl: appIconUrl || fallbackLensIcon,
            name: internalAppName ?? "",
            prodStatus: AppListItemModel?.getIsCamkitEnabled(versions, prodLiveVersion ?? -1),
            prodLiveVersion: AppListItemModel?.getVersionName(versions, prodLiveVersion ?? -1) ?? "",
            stagingLiveVersion: AppListItemModel?.getVersionName(versions, stagingLiveVersion ?? -1) ?? "",
            stageStatus: AppListItemModel?.getIsCamkitEnabled(versions, stagingLiveVersion ?? -1),
            organization: "",
            appId: appId ?? "",
            versions: versions ?? [],
            authorizedOperations:
                appAuthorizedOperations
                    ?.find((op) => op.authOperation == MyLensesAuthOperation.AuthOperationSnapKitApps)
                    ?.authOperationTypes?.flatMap((authOpType) =>
                        typeof authOpType === "string" ? [authOpType] : [],
                    ) || [],
            platformType: platformType ?? MyLensesPlatformType.PlatformTypeMobile,
        };
    }

    public static getIsCamkitEnabled(versions: OrganizationApps["versions"], liveVersion: number) {
        if (!versions?.length) {
            return AppVersionStatus.DISABLED;
        }

        const [enabledVersion] = versions?.filter((version) => version.version === liveVersion);

        if (enabledVersion == null || liveVersion === -1) {
            return AppVersionStatus.UNKNOWN;
        }

        const featureList = enabledVersion?.features?.filter((feature) =>
            feature?.featureScopeName?.includes("camera"),
        );
        return featureList?.[0]?.enabled ? AppVersionStatus.ENABLED : AppVersionStatus.DISABLED;
    }

    public static getVersionName(versions: OrganizationApps["versions"], liveVersion: number) {
        if (!versions?.length) {
            return AppVersionStatus.DISABLED;
        }

        const [enabledVersion] = versions?.filter((version) => version.version === liveVersion);

        if (enabledVersion == null || liveVersion === -1) {
            return "Version is unset";
        }
        return enabledVersion.versionName;
    }
}
